import { useEffect, useMemo, useState } from "react";
import { Badge, Card, EmptyState, IndexTable, InlineStack, Page, Spinner, Tabs, Text, TextField } from "@shopify/polaris";
import { useNavigate, useSearchParams } from "react-router-dom";
import AppWrapper from "../AppWrapper";
import supabase from "../../supabase-client";
import { UserAuth } from "../../context/AuthContext";
import { money } from "../../utility/egresos";
import "./AllEgresos.css";

// Egresos, by the one question each tab answers:
//   Por pagar   what is still owed, once, by date: Atrasados first, then a
//               header per week with its total, then what has no date yet.
//   Pendientes  anything that needs a step other than paying (approve,
//               account for an advance, recover a deposit, a missing REP, the
//               provider's balance disagreeing with ours, or close it).
//   Cerrados    history.
// Balances come from egreso_balances and REPs from egreso_rep_status; nothing
// is summed here except the totals shown. Someone who can only request
// payments (egresos.request) sees "Mis solicitudes": just their own egresos.

const TABS = [
  { id: "pagar", content: "Por pagar" },
  { id: "proveedor", content: "Por proveedor" },
  { id: "pendientes", content: "Pendientes" },
  { id: "cerrados", content: "Cerrados" },
];

// The next dated payment: planned lines are covered in order by what has been
// paid (as on the egreso page); with no dated line left, the egreso's own
// due_date. `owed` is what is still to pay overall.
function nextPayment(e, b, plan, owed) {
  let paidLeft = Number(b.net_paid_cents || 0);
  for (const p of plan) {
    const covered = Math.min(paidLeft, Number(p.amount_cents));
    paidLeft -= covered;
    const left = Number(p.amount_cents) - covered;
    if (left > 0 && p.due_date) return { date: p.due_date, cents: left };
  }
  return e.due_date && owed > 0 ? { date: e.due_date, cents: owed } : null;
}

// What an open egreso needs besides paying, as badge labels.
function todo(e, b, rep, owed) {
  const out = [];
  if (e.approval === "pendiente") out.push({ label: "Aprobar", tone: "attention" });
  if (b.por_comprobar_cents > 0) out.push({ label: "Comprobar", tone: "attention" });
  if (b.deposito_cents > 0) out.push({ label: "Recuperar depósito", tone: "attention" });
  if (rep?.missing_reps > 0) out.push({ label: "Falta REP", tone: "warning" });
  if ((rep && Math.abs(Number(rep.provider_diff_cents)) > 100) || rep?.unmatched_reps > 0)
    out.push({ label: "Diferencia con proveedor", tone: "critical" });
  if (!out.length && owed <= 0) out.push({ label: "Cerrar", tone: "success" });
  return out;
}

const atNoon = (d) => new Date(`${d.slice(0, 10)}T12:00`);
const shortDate = (d) => atNoon(d).toLocaleDateString("es-MX", { day: "numeric", month: "short" });
const todayStr = () => new Date().toLocaleDateString("en-CA");
// Monday of the date's week, as YYYY-MM-DD.
const weekOf = (d) => {
  const x = atNoon(d);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x.toLocaleDateString("en-CA");
};

// One line, "…" when it does not fit (see AllEgresos.css).
const Cell = ({ children, tone, numeric, end }) => (
  <IndexTable.Cell>
    <div className={end ? "eg-line eg-end" : "eg-line"}>
      <Text as="span" tone={tone} numeric={numeric} truncate>{children}</Text>
    </div>
  </IndexTable.Cell>
);

const COLUMNS = {
  pagar: [{ title: "Fecha" }, { title: "Concepto" }, { title: "A quién" }, { title: "Auto" }, { title: "Por pagar", alignment: "end" }],
  proveedor: [{ title: "Proveedor" }, { title: "Egresos", alignment: "end" }, { title: "Próx. vence" }, { title: "Por pagar", alignment: "end" }],
  pendientes: [{ title: "#" }, { title: "Concepto" }, { title: "A quién" }, { title: "Auto" }, { title: "Qué falta" }],
  cerrados: [{ title: "#" }, { title: "Concepto" }, { title: "A quién" }, { title: "Auto" }, { title: "Costo", alignment: "end" }],
};

export default function AllEgresos() {
  const navigate = useNavigate();
  const { session, hasPermission } = UserAuth();
  const onlyMine = !hasPermission?.("services.create") && !hasPermission?.("services.pay");
  const myId = session?.user?.id;
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState(null);
  const tabFromUrl = Math.max(TABS.findIndex((t) => t.id === searchParams.get("tab")), 0);
  const [tab, setTab] = useState(tabFromUrl);
  useEffect(() => setTab(tabFromUrl), [tabFromUrl]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    (async () => {
      const [{ data: eg }, { data: bal }, { data: pay }, { data: plans }, { data: reps }] = await Promise.all([
        supabase
          .from("egresos")
          .select("*, car:cars(plate)")
          .order("created_at", { ascending: false })
          .limit(500),
        supabase.from("egreso_balances").select("*"),
        supabase.from("egreso_payees").select("egreso_id, payee_name, payee_nickname"),
        supabase.from("egreso_planned_payments").select("egreso_id, position, amount_cents, due_date").order("position"),
        supabase.from("egreso_rep_status").select("*"),
      ]);
      const b = Object.fromEntries((bal || []).map((x) => [x.egreso_id, x]));
      const p = Object.fromEntries((pay || []).map((x) => [x.egreso_id, x.payee_nickname || x.payee_name]));
      const r = Object.fromEntries((reps || []).map((x) => [x.egreso_id, x]));
      const planOf = {};
      for (const x of plans || []) (planOf[x.egreso_id] ||= []).push(x);
      setRows(
        (eg || [])
          .filter((e) => !onlyMine || e.created_by === myId)
          .map((e) => {
            const bb = b[e.id] || {};
            const owed = e.closed_at
              ? 0
              : Math.max(Number(bb.por_pagar_cents || 0), Number(e.committed_cents || 0) - Number(bb.net_paid_cents || 0));
            const next = owed > 0 ? nextPayment(e, bb, planOf[e.id] || [], owed) : null;
            return {
              ...e,
              b: bb,
              payee_name: p[e.id],
              owed,
              next,
              todo: e.closed_at ? [] : todo(e, bb, r[e.id], owed),
            };
          })
      );
    })();
  }, [onlyMine, myId]);

  const lists = useMemo(() => {
    const all = rows || [];
    // A request waiting for approval is not owed yet.
    const payable = all.filter((r) => !r.closed_at && r.approval !== "pendiente" && r.owed > 0);
    // What we owe rolled up by provider; earliest pending payment wins as "next".
    const provMap = new Map();
    for (const r of payable) {
      const name = r.payee_name || "—";
      const g = provMap.get(name) || { id: name, name, count: 0, owed: 0, next: null };
      g.count += 1;
      g.owed += r.owed;
      const d = r.next?.date || null;
      if (d && (!g.next || d < g.next)) g.next = d;
      provMap.set(name, g);
    }
    const byProvider = [...provMap.values()].sort((a, b) => b.owed - a.owed);
    return {
      pagar: payable,
      proveedor: byProvider,
      pendientes: all.filter((r) => !r.closed_at && r.todo.length > 0),
      cerrados: all.filter((r) => !!r.closed_at),
    };
  }, [rows]);

  const tabs = TABS.map((t) => {
    const n = lists[t.id].length;
    return { id: t.id, content: n && t.id !== "cerrados" ? `${t.content} (${n})` : t.content };
  });
  const tabId = TABS[tab].id;

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (tabId === "proveedor")
      return lists.proveedor.filter((r) => !q || r.name.toLowerCase().includes(q));
    const out = lists[tabId].filter(
      (r) =>
        !q ||
        [r.title, r.payee_name, r.category, r.car?.plate, r.sublease_plate, String(r.id)]
          .filter(Boolean)
          .some((s) => s.toLowerCase().includes(q))
    );
    if (tabId !== "pagar") return out;
    // By date; what has no date goes last.
    return out.sort((a, b) => (a.next?.date || "9999").localeCompare(b.next?.date || "9999") || a.id - b.id);
  }, [lists, tabId, query]);

  const today = todayStr();
  // Por pagar groups: Atrasados, one per week (Monday), Sin fecha.
  const groupOf = (r) => (!r.next ? "sin" : r.next.date < today ? "atrasados" : weekOf(r.next.date));
  const amountOf = (r) => (r.next ? r.next.cents : r.owed);
  const groupTotals = {};
  if (tabId === "pagar") for (const r of visible) groupTotals[groupOf(r)] = (groupTotals[groupOf(r)] || 0) + amountOf(r);
  const groupTitle = (g) => (g === "atrasados" ? "Atrasados" : g === "sin" ? "Sin fecha" : `Semana del ${shortDate(g)}`);
  const total =
    tabId === "pagar" || tabId === "proveedor"
      ? visible.reduce((s, r) => s + r.owed, 0)
      : null;

  const rowsOut = tabId === "proveedor"
    ? visible.map((p, i) => {
        const over = p.next && p.next < today;
        return (
          <IndexTable.Row
            key={p.name}
            id={p.name}
            position={i}
            onClick={() => { setQuery(p.name); setTab(0); }}
          >
            <Cell>{p.name}</Cell>
            <Cell numeric end>{p.count}</Cell>
            <Cell tone={over ? "critical" : p.next ? undefined : "subdued"}>
              {p.next ? shortDate(p.next) : "—"}
            </Cell>
            <Cell numeric end>{money(p.owed)}</Cell>
          </IndexTable.Row>
        );
      })
    : visible.flatMap((r, i) => {
    const open = () => navigate(`/egresos/${r.id}`);
    const common = [
      <Cell key="t">{r.title}</Cell>,
      <Cell key="p">{r.payee_name || "—"}</Cell>,
      <Cell key="a">{r.car?.plate || r.sublease_plate || "—"}</Cell>,
    ];
    if (tabId === "pagar") {
      const g = groupOf(r);
      const header = (i === 0 || groupOf(visible[i - 1]) !== g) && (
        <IndexTable.Row key={`g-${g}`} id={`g-${g}`} position={i} rowType="subheader">
          <IndexTable.Cell colSpan={5} scope="colgroup" as="th" id={`g-${g}`}>
            <InlineStack align="space-between" wrap={false}>
              <Text fontWeight="semibold" tone={g === "atrasados" ? "critical" : undefined}>{groupTitle(g)}</Text>
              <Text numeric fontWeight="semibold" tone={g === "atrasados" ? "critical" : undefined}>{money(groupTotals[g])}</Text>
            </InlineStack>
          </IndexTable.Cell>
        </IndexTable.Row>
      );
      const row = (
        <IndexTable.Row key={r.id} id={String(r.id)} position={i} onClick={open}>
          <Cell tone={g === "atrasados" ? "critical" : r.next ? undefined : "subdued"}>{r.next ? shortDate(r.next.date) : "—"}</Cell>
          {common}
          <Cell numeric end>{money(amountOf(r))}</Cell>
        </IndexTable.Row>
      );
      return header ? [header, row] : [row];
    }
    return [
      <IndexTable.Row key={r.id} id={String(r.id)} position={i} onClick={open}>
        <Cell tone="subdued">{r.id}</Cell>
        {common}
        {tabId === "pendientes" ? (
          <IndexTable.Cell>
            <div className="eg-line">
              <InlineStack gap="100" wrap={false}>
                {r.todo.map((t) => <Badge key={t.label} tone={t.tone}>{t.label}</Badge>)}
              </InlineStack>
            </div>
          </IndexTable.Cell>
        ) : (
          <Cell numeric end>{money(Math.max(r.b.cost_cents || 0, r.committed_cents || 0))}</Cell>
        )}
      </IndexTable.Row>,
    ];
  });

  return (
    <AppWrapper>
      <Page
        title={onlyMine ? "Mis solicitudes" : "Egresos"}
        subtitle={total ? `${money(total)} por pagar` : undefined}
        primaryAction={{ content: onlyMine ? "Nueva solicitud" : "Nuevo egreso", onAction: () => navigate("/egresos/new") }}
      >
        <Card padding="0">
          <Tabs tabs={tabs} selected={tab} onSelect={setTab} />
          <div style={{ padding: 12 }}>
            <TextField
              label="Buscar"
              labelHidden
              value={query}
              onChange={setQuery}
              placeholder="Buscar por concepto, a quién, placa o #"
              autoComplete="off"
              clearButton
              onClearButtonClick={() => setQuery("")}
            />
          </div>
          {rows === null ? (
            <div style={{ padding: 24, textAlign: "center" }}><Spinner size="small" /></div>
          ) : visible.length === 0 ? (
            <EmptyState heading={tabId === "pendientes" ? "Nada pendiente" : "Nada aquí"} image="">
              <p>{tabId === "pagar" || tabId === "proveedor" ? "No hay nada por pagar." : tabId === "pendientes" ? "Todo al corriente." : "Aún no hay egresos cerrados."}</p>
            </EmptyState>
          ) : (
            <div className={`egresos-table egresos-table--${tabId}`}>
              <IndexTable
                resourceName={{ singular: "egreso", plural: "egresos" }}
                itemCount={visible.length}
                selectable={false}
                headings={COLUMNS[tabId]}
              >
                {rowsOut}
              </IndexTable>
            </div>
          )}
        </Card>
      </Page>
    </AppWrapper>
  );
}
